import { registryMetadata } from "./metadata.js";
import type { PlotTraceMeta } from "./plotDataOrganizer.js";
import { type ThinningInfo, thinningNotice } from "./thinning.js";

type PlotDataWithMeta = Plotly.PlotData & { meta?: Partial<PlotTraceMeta> };
type VisibleField = (typeof registryMetadata)[number]["fields"][number];

/** Plotted traces with data, excluding the ±360° wind direction copies */
function getPrimaryPlotData(
	plotElement: Plotly.PlotlyHTMLElement,
): PlotDataWithMeta[] {
	return (plotElement.data as PlotDataWithMeta[]).filter(
		(trace) =>
			trace && Array.isArray(trace.y) && trace.meta?.isWrappedTrace !== true,
	);
}

/**
 * Table columns: the timestamp, then one per plotted field in trace order, so
 * user-hidden fields drop out and columns line up with traces. Falls back to
 * the registry's visible fields when traces carry no field names.
 */
function getVisibleFields(
	stationTypeId: number,
	plotData?: PlotDataWithMeta[],
): VisibleField[] {
	const format = registryMetadata[stationTypeId];
	if (!format) {
		console.error(`Table ID ${stationTypeId} not found in registry metadata`);
		return [];
	}
	const registryVisible = format.fields.filter((f) => f.visible !== false);
	const fieldNames = (plotData ?? [])
		.map((trace) => trace.meta?.fieldName)
		.filter((name): name is string => Boolean(name));
	if (fieldNames.length === 0) return registryVisible;

	const byName = new Map(format.fields.map((f) => [f.name, f]));
	const plotted = fieldNames
		.map((name) => byName.get(name))
		.filter((f): f is VisibleField => f !== undefined && f.visible !== false);
	const timestamp = format.fields.find((f) => f.role === "timestamp");
	return timestamp ? [timestamp, ...plotted] : plotted;
}

/** Notice above the table explaining min/max thinning; hidden when not thinned */
function updateThinningNotice(thinning: ThinningInfo | null): void {
	const tableDiv = document.getElementById("tableDiv");
	if (!tableDiv) return;
	let notice = document.getElementById("thinningNotice");
	if (!notice) {
		notice = document.createElement("p");
		notice.id = "thinningNotice";
		notice.classList.add("text-muted", "small", "mb-1");
		tableDiv.before(notice);
	}
	notice.hidden = !thinning;
	notice.textContent = thinning ? thinningNotice(thinning) : "";
}

export function createTable(
	stationTypeId: number,
	plotData?: PlotDataWithMeta[],
): HTMLDivElement {
	const tableDiv = document.getElementById("tableDiv");
	if (!tableDiv) {
		console.error('No div with id "tableDiv" found.');
		return document.createElement("div");
	}

	const existingTable = tableDiv.querySelector("table");
	if (existingTable) {
		existingTable.remove();
	}

	const table = document.createElement("table");
	table.classList.add("table", "table-striped", "table-sm");
	table.setAttribute("role", "table");
	tableDiv.append(table);
	const thead = document.createElement("thead");
	const trHead = document.createElement("tr");
	thead.append(trHead);
	table.append(thead);
	const visibleFields = getVisibleFields(stationTypeId, plotData);
	for (const field of visibleFields) {
		const thHead = document.createElement("th");
		thHead.scope = "col";
		thHead.innerText = field.name;
		trHead.append(thHead);
	}
	const tbody = document.createElement("tbody");
	tbody.id = "dataTableTbody";
	table.append(tbody);
	return tbody;
}
let currentTableId: number | null = null;
let currentFieldSignature: string | null = null;
export function populateTable(
	divId: string,
	startDateString: string,
	endDateString: string | null,
	tableId: number,
	thinning: ThinningInfo | null = null,
): void {
	updateThinningNotice(thinning);
	const plotElement = document.getElementById(
		divId,
	) as Plotly.PlotlyHTMLElement;
	if (!plotElement) {
		console.error(`${divId} is not an id in the current document`);
		return;
	}

	const primaryPlotData = getPrimaryPlotData(plotElement);
	const visibleFields = getVisibleFields(tableId, primaryPlotData);
	const fieldSignature = visibleFields.map((field) => field.name).join("|");

	let tbody: HTMLElement | null;
	if (
		currentTableId !== tableId ||
		currentFieldSignature !== fieldSignature ||
		!document.getElementById("dataTableTbody")
	) {
		currentTableId = tableId;
		currentFieldSignature = fieldSignature;
		tbody = createTable(tableId, primaryPlotData);
	} else {
		tbody = document.getElementById("dataTableTbody");
	}
	if (!tbody) {
		console.error('No tbody "dataTableTbody".');
		return;
	}
	tbody.innerHTML = "";

	if (!currentTableId) {
		console.error("No current table ID set.");
		return;
	}
	if (primaryPlotData.length > 0) {
		const firstData = primaryPlotData[0];
		if (firstData.x && Array.isArray(firstData.x)) {
			const timeValues = firstData.x;
			// "min"/"max" per row when thinned (see thinning.ts)
			const rowKinds = thinning
				? (firstData.customdata as (string | null)[] | undefined)
				: undefined;
			let seenData = false;
			const startDate = new Date(startDateString);
			const endDate = endDateString ? new Date(endDateString) : new Date();
			for (let i = 0; i < timeValues.length; i++) {
				const values = timeValues[i];
				if (values && values > startDate && values < endDate) {
					seenData = true;
					const rowKind = rowKinds?.[i];
					const tr = document.createElement("tr");
					tbody.append(tr);
					// Use registryMetadata for station type field order (visible fields only)
					for (let k = 0; k < visibleFields.length; k++) {
						const td = document.createElement("td");
						let text: string | null = null;
						if (k === 0) {
							// First column is timestamp
							text = values.toLocaleString("en-NZ");
							if (rowKind) text += ` — ${rowKind}`;
						} else if (
							rowKind === "max" &&
							visibleFields[k].role === "rain_total"
						) {
							// Bucket total is shown once, on the min row, so the column sums correctly
						} else {
							const dataset = primaryPlotData[k - 1];
							if (!dataset?.y) continue;
							const datasetText = dataset.y[i];
							if (datasetText !== null && !Number.isNaN(+datasetText)) {
								text = datasetText.toString();
								td.innerText = (+text).toFixed(2);
								if (dataset.error_y && dataset.error_y.type === "data") {
									const errText = dataset.error_y.array[i];
									if (errText) td.innerText += `±${(+errText).toFixed(1)}`;
								}
							}
						}
						if (k === 0) td.innerText = text ? String(text) : "";
						tr.append(td);
					}
				} else if (seenData) {
					// Only add a break row after first data and if previous wasn't a break
					const tr = document.createElement("tr");
					const td = document.createElement("td");
					td.setAttribute("data-break-row", "true");
					td.style.lineHeight = "10px";
					td.colSpan = visibleFields.length;
					td.style.backgroundColor = "#AAAAAA";
					tr.append(td);
					tbody.append(tr);
				}
			}
			// Remove trailing break rows
			while (
				tbody.lastElementChild &&
				tbody.lastElementChild.childNodes.length === 1 &&
				(tbody.lastElementChild.firstChild as HTMLElement).getAttribute(
					"data-break-row",
				) === "true"
			) {
				tbody.removeChild(tbody.lastElementChild);
			}
		}
	}
}
